import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppSidebar from "./AppSidebar";
import { useExam } from "../context/ExamContext";
import { getExams } from "../api/client";
import {
  getPracticeQuestions,
  submitPracticeAnswer,
} from "../api/practiceClient";
import "../styles/practice.css";

function getErrorMessage(error, fallback) {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
}

function normalizeQuestion(question) {
  return {
    ...question,
    question_text: question.question_text || question.question || question.text || "",
    question_type: String(question.question_type || "MCQ").toUpperCase(),
    question_format: String(
      question.question_format || question.question_type || "MCQ"
    ).toUpperCase(),
    options: Array.isArray(question.options) ? question.options : [],
    answer_data:
      question.answer_data && typeof question.answer_data === "object"
        ? question.answer_data
        : {},
  };
}

function isChoiceQuestion(question) {
  return (
    question.question_type === "MCQ" ||
    question.question_format === "MCQ" ||
    question.question_format === "DROPDOWN"
  );
}

function isMultiSelectQuestion(question) {
  return question.question_format === "MULTI_SELECT";
}

function isEssayQuestion(question) {
  return (
    question.question_type === "ESSAY" ||
    question.question_format === "TEXT"
  );
}

export default function Practice() {
  const navigate = useNavigate();
  const { selectedExam, selectedCourse } = useExam();

  const [exams, setExams] = useState([]);
  const [examId, setExamId] = useState(
    selectedExam?.id ? String(selectedExam.id) : ""
  );
  const [questionCount, setQuestionCount] = useState("10");
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [feedback, setFeedback] = useState({});
  const [loadingExams, setLoadingExams] = useState(true);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const currentQuestion = questions[currentIndex] || null;
  const currentAnswer = currentQuestion ? answers[currentQuestion.id] : null;
  const currentFeedback = currentQuestion ? feedback[currentQuestion.id] : null;

  useEffect(() => {
    let active = true;

    async function loadExams() {
      setLoadingExams(true);
      try {
        const response = await getExams();
        const raw = response?.data;
        const list = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.results)
          ? raw.results
          : Array.isArray(raw?.exams)
          ? raw.exams
          : [];

        if (active) {
          setExams(list);
          if (!examId && selectedExam?.id) {
            setExamId(String(selectedExam.id));
          } else if (!examId && selectedCourse?.id) {
            const match = list.find(
              (exam) =>
                Number(exam.course) === Number(selectedCourse.id) ||
                Number(exam.course_id) === Number(selectedCourse.id)
            );
            if (match) setExamId(String(match.id));
          }
        }
      } catch (loadError) {
        if (active) {
          setError(
            getErrorMessage(
              loadError,
              "Could not load the exam list. You can retry after checking your connection."
            )
          );
        }
      } finally {
        if (active) setLoadingExams(false);
      }
    }

    loadExams();
    return () => {
      active = false;
    };
    // Initial exam choice is intentional; do not reload exams on every selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadQuestions = useCallback(async () => {
    if (!examId) {
      setError("Choose an exam before loading practice questions.");
      return;
    }

    setLoadingQuestions(true);
    setError("");
    setNotice("");
    setQuestions([]);
    setAnswers({});
    setFeedback({});
    setCurrentIndex(0);

    try {
      const response = await getPracticeQuestions({
        examId: Number(examId),
        count: Number(questionCount) || 10,
      });
      const data = response?.data || {};
      const rawQuestions = Array.isArray(data)
        ? data
        : Array.isArray(data.questions)
        ? data.questions
        : [];

      if (!rawQuestions.length) {
        throw new Error("No practice questions were returned for this exam.");
      }

      setQuestions(rawQuestions.map(normalizeQuestion));
      setNotice(`${rawQuestions.length} practice question(s) loaded.`);
    } catch (loadError) {
      setError(
        getErrorMessage(
          loadError,
          "Unable to load practice questions. Check the backend endpoint and try again."
        )
      );
    } finally {
      setLoadingQuestions(false);
    }
  }, [examId, questionCount]);

  const answeredCount = useMemo(
    () =>
      questions.filter((question) => {
        const answer = answers[question.id];
        if (answer == null) return false;
        if (typeof answer === "string") return answer.trim().length > 0;
        if (Array.isArray(answer)) return answer.length > 0;
        return Boolean(answer.selected_option_id || answer.essay_answer || answer.value);
      }).length,
    [questions, answers]
  );

  const setAnswer = (questionId, value) => {
    setAnswers((previous) => ({ ...previous, [questionId]: value }));
    setNotice("");
  };

  const toggleMultiSelect = (questionId, optionId) => {
    const existing = Array.isArray(answers[questionId]) ? answers[questionId] : [];
    const next = existing.includes(optionId)
      ? existing.filter((id) => id !== optionId)
      : [...existing, optionId];
    setAnswer(questionId, next);
  };

  const handleSubmitAnswer = async () => {
    if (!currentQuestion) return;

    const answer = answers[currentQuestion.id];
    const question = currentQuestion;
    let payload = {};

    if (isChoiceQuestion(question)) {
      const optionId =
        answer && typeof answer === "object" ? answer.selected_option_id : answer;
      if (!optionId) {
        setError("Please select an answer before submitting.");
        return;
      }
      payload = { selected_option_id: optionId };
    } else if (isMultiSelectQuestion(question)) {
      if (!Array.isArray(answer) || answer.length === 0) {
        setError("Please select at least one option before submitting.");
        return;
      }
      payload = { answer_data: { selected_options: answer } };
    } else if (isEssayQuestion(question)) {
      const essay = typeof answer === "string" ? answer : answer?.essay_answer || "";
      if (!essay.trim()) {
        setError("Please enter your answer before submitting.");
        return;
      }
      payload = { essay_answer: essay, answer_data: { answer: essay, value: essay } };
    } else {
      const value =
        typeof answer === "string"
          ? answer
          : answer && typeof answer === "object"
          ? answer.value ?? answer.answer ?? ""
          : "";
      if (!String(value).trim()) {
        setError("Please enter an answer before submitting.");
        return;
      }
      payload = { answer_data: { answer: value, value } };
    }

    setSubmitting(true);
    setError("");
    setNotice("");
    try {
      const response = await submitPracticeAnswer(question.id, payload);
      setFeedback((previous) => ({
        ...previous,
        [question.id]: response?.data || {},
      }));
      setNotice("Answer submitted. Review the feedback below.");
    } catch (submitError) {
      setError(
        getErrorMessage(
          submitError,
          "Could not submit this answer. Please try again."
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderAnswerInput = () => {
    if (!currentQuestion) return null;
    const question = currentQuestion;
    const feedbackReceived = Boolean(currentFeedback);

    if (isChoiceQuestion(question)) {
      const selectedId =
        currentAnswer && typeof currentAnswer === "object"
          ? currentAnswer.selected_option_id
          : currentAnswer;
      return (
        <div className="practice-options">
          {question.options.map((option, index) => {
            const optionId = option.id;
            const correctOption = currentFeedback?.feedback?.options?.find(
              (item) => Number(item.id) === Number(optionId)
            );
            return (
              <label
                className={`practice-option ${
                  Number(selectedId) === Number(optionId) ? "selected" : ""
                } ${
                  feedbackReceived && correctOption?.is_correct ? "correct" : ""
                }`}
                key={optionId ?? index}
              >
                <input
                  type="radio"
                  name={`practice-${question.id}`}
                  checked={Number(selectedId) === Number(optionId)}
                  disabled={feedbackReceived}
                  onChange={() =>
                    setAnswer(question.id, { selected_option_id: optionId })
                  }
                />
                <span className="practice-option-letter">
                  {String.fromCharCode(65 + index)}
                </span>
                <span>{option.option_text || option.text || `Option ${index + 1}`}</span>
                {feedbackReceived && correctOption?.is_correct && (
                  <i className="bi bi-check-circle-fill practice-correct-icon" />
                )}
              </label>
            );
          })}
        </div>
      );
    }

    if (isMultiSelectQuestion(question)) {
      const selectedIds = Array.isArray(currentAnswer) ? currentAnswer : [];
      return (
        <div className="practice-options">
          {question.options.map((option, index) => {
            const optionId = option.id;
            const correctOption = currentFeedback?.feedback?.options?.find(
              (item) => Number(item.id) === Number(optionId)
            );
            return (
              <label
                className={`practice-option ${
                  selectedIds.includes(optionId) ? "selected" : ""
                } ${
                  feedbackReceived && correctOption?.is_correct ? "correct" : ""
                }`}
                key={optionId ?? index}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.includes(optionId)}
                  disabled={feedbackReceived}
                  onChange={() => toggleMultiSelect(question.id, optionId)}
                />
                <span className="practice-option-letter">
                  {String.fromCharCode(65 + index)}
                </span>
                <span>{option.option_text || option.text || `Option ${index + 1}`}</span>
              </label>
            );
          })}
        </div>
      );
    }

    if (isEssayQuestion(question)) {
      const value =
        typeof currentAnswer === "string"
          ? currentAnswer
          : currentAnswer?.essay_answer || "";
      return (
        <textarea
          className="practice-answer-textarea"
          value={value}
          disabled={feedbackReceived}
          onChange={(event) => setAnswer(question.id, event.target.value)}
          placeholder="Write your answer here..."
          rows={7}
        />
      );
    }

    const value =
      typeof currentAnswer === "string"
        ? currentAnswer
        : currentAnswer?.value ?? currentAnswer?.answer ?? "";
    return (
      <input
        className="practice-answer-input"
        type={question.question_format === "NUMERIC" ? "number" : "text"}
        value={value}
        disabled={feedbackReceived}
        onChange={(event) => setAnswer(question.id, event.target.value)}
        placeholder="Enter your answer"
      />
    );
  };

  return (
    <div className="oes-page-full practice-page">
      <div className="oes-home-shell d-flex flex-row">
        <AppSidebar />
        <main className="flex-grow-1 oes-page container-fluid px-3 px-md-4 py-4">
          <div className="practice-heading">
            <div>
              <span className="practice-eyebrow">LEARN AT YOUR OWN PACE</span>
              <h1>Practice Exam</h1>
              <p>Try questions from the question bank and review feedback after each answer.</p>
            </div>
            <button className="practice-back-button" onClick={() => navigate("/home")}>
              <i className="bi bi-arrow-left" /> Back to Home
            </button>
          </div>

          <section className="practice-setup-card">
            <div className="practice-card-heading">
              <div className="practice-icon-tile"><i className="bi bi-journal-check" /></div>
              <div>
                <h2>Set up your practice</h2>
                <p>Choose an exam and how many questions you want to practise.</p>
              </div>
            </div>

            <div className="practice-form-grid">
              <label className="practice-field">
                <span>Exam</span>
                <select
                  value={examId}
                  onChange={(event) => setExamId(event.target.value)}
                  disabled={loadingExams || loadingQuestions}
                >
                  <option value="">
                    {loadingExams ? "Loading exams..." : "Choose an exam"}
                  </option>
                  {exams.map((exam) => (
                    <option key={exam.id} value={exam.id}>
                      {exam.name || exam.title || `Exam ${exam.id}`}
                    </option>
                  ))}
                </select>
              </label>

              <label className="practice-field">
                <span>Number of questions</span>
                <select
                  value={questionCount}
                  onChange={(event) => setQuestionCount(event.target.value)}
                  disabled={loadingQuestions}
                >
                  <option value="5">5 questions</option>
                  <option value="10">10 questions</option>
                  <option value="20">20 questions</option>
                  <option value="50">50 questions</option>
                </select>
              </label>

              <button
                className="practice-primary-button"
                onClick={loadQuestions}
                disabled={loadingQuestions || loadingExams || !examId}
              >
                {loadingQuestions ? (
                  <><span className="practice-spinner" /> Loading...</>
                ) : (
                  <><i className="bi bi-play-fill" /> Load Practice Questions</>
                )}
              </button>
            </div>
          </section>

          {error && (
            <div className="practice-alert practice-alert-error" role="alert">
              <i className="bi bi-exclamation-circle-fill" />
              <span>{error}</span>
              <button onClick={() => setError("")} aria-label="Dismiss error">×</button>
            </div>
          )}
          {notice && !error && (
            <div className="practice-alert practice-alert-success" role="status">
              <i className="bi bi-check-circle-fill" />
              <span>{notice}</span>
            </div>
          )}

          {questions.length > 0 && (
            <div className="practice-workspace">
              <section className="practice-question-card">
                <div className="practice-question-topline">
                  <span className="practice-question-count">
                    Question {currentIndex + 1} <span>/ {questions.length}</span>
                  </span>
                  <span className="practice-marks">
                    {currentQuestion?.marks ?? 1} mark(s)
                  </span>
                </div>

                <div className="practice-progress-track">
                  <div
                    className="practice-progress-fill"
                    style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
                  />
                </div>

                {currentQuestion?.section_name && (
                  <div className="practice-section-label">{currentQuestion.section_name}</div>
                )}
                <h2 className="practice-question-title">
                  {currentQuestion?.question_text || "Question text is not available."}
                </h2>
                <div className="practice-answer-area">{renderAnswerInput()}</div>

                {currentFeedback && (
                  <div
                    className={`practice-feedback ${
                      currentFeedback.correct ? "is-correct" : "is-incorrect"
                    }`}
                  >
                    <div className="practice-feedback-title">
                      <i className={`bi ${currentFeedback.correct ? "bi-check-circle-fill" : "bi-info-circle-fill"}`} />
                      {currentFeedback.correct ? "Correct answer" : "Answer submitted"}
                    </div>
                    <p>
                      Marks awarded: <strong>{currentFeedback.marks_awarded ?? 0}</strong>
                      {" / "}
                      {currentFeedback.maximum_marks ?? currentQuestion?.marks ?? 1}
                    </p>
                    {currentFeedback.feedback?.explanation && (
                      <div className="practice-explanation">
                        <strong>Explanation</strong>
                        <p>{currentFeedback.feedback.explanation}</p>
                      </div>
                    )}
                    {currentFeedback.feedback?.correct_answer != null && (
                      <div className="practice-explanation">
                        <strong>Correct answer</strong>
                        <p>
                          {typeof currentFeedback.feedback.correct_answer === "string"
                            ? currentFeedback.feedback.correct_answer
                            : JSON.stringify(currentFeedback.feedback.correct_answer)}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div className="practice-question-actions">
                  <button
                    className="practice-secondary-button"
                    onClick={() => setCurrentIndex((index) => Math.max(0, index - 1))}
                    disabled={currentIndex === 0 || submitting}
                  >
                    <i className="bi bi-arrow-left" /> Previous
                  </button>

                  {!currentFeedback ? (
                    <button
                      className="practice-primary-button"
                      onClick={handleSubmitAnswer}
                      disabled={submitting}
                    >
                      {submitting ? "Submitting..." : "Check Answer"}
                      {!submitting && <i className="bi bi-check2" />}
                    </button>
                  ) : currentIndex < questions.length - 1 ? (
                    <button
                      className="practice-primary-button"
                      onClick={() => setCurrentIndex((index) => index + 1)}
                      disabled={submitting}
                    >
                      Next Question <i className="bi bi-arrow-right" />
                    </button>
                  ) : (
                    <button
                      className="practice-primary-button"
                      onClick={() => {
                        setQuestions([]);
                        setAnswers({});
                        setFeedback({});
                        setCurrentIndex(0);
                        setNotice("Practice session completed. You can start another set whenever you're ready.");
                      }}
                    >
                      Finish Practice <i className="bi bi-check2-circle" />
                    </button>
                  )}
                </div>
              </section>

              <aside className="practice-session-card">
                <h3>Your session</h3>
                <div className="practice-stat">
                  <span>Questions</span><strong>{questions.length}</strong>
                </div>
                <div className="practice-stat">
                  <span>Answered</span><strong>{answeredCount} / {questions.length}</strong>
                </div>
                <div className="practice-stat">
                  <span>Reviewed</span><strong>{Object.keys(feedback).length} / {questions.length}</strong>
                </div>
                <div className="practice-mini-grid">
                  {questions.map((question, index) => (
                    <button
                      key={question.id}
                      className={`practice-mini-question ${
                        index === currentIndex ? "active" : ""
                      } ${
                        feedback[question.id] ? "reviewed" : ""
                      }`}
                      onClick={() => setCurrentIndex(index)}
                      aria-label={`Go to question ${index + 1}`}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
                <p className="practice-session-note">
                  Your practice answers are separate from your official exam attempt and result.
                </p>
              </aside>
            </div>
          )}

          {!questions.length && !loadingQuestions && !error && (
            <section className="practice-empty-state">
              <div className="practice-empty-icon"><i className="bi bi-journal-text" /></div>
              <h2>Ready when you are</h2>
              <p>Load a set of practice questions to begin. Your official exam progress will not be changed.</p>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}

